import React from 'react'
import { Modal } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { CurrentUserType, logoutUser } from 'src/ducks/auth'
import Amplitude from 'src/utils/amplitudeHelper'
import { SIZES } from 'src/styles/theme'

export const ATTENDANCE_NOTICE =
  'Kindly remain in the Plenary Hall throughout each session. Please be advised that attendance will only be recorded once participants enter the Plenary Hall option from the main menu and remain there until the end of each session.'

const StyledModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: 20px;
    padding: 32px 24px 24px;
    font-family: 'Din', 'DIN', sans-serif;
    text-align: center;
    color: #262c65;
  }

  h5 {
    margin: 0 0 16px;
    font-size: 24px;
    font-weight: 700;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    font-size: 16px;
    line-height: 1.5;
  }

  .actions {
    margin-top: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  button {
    min-width: 130px;
    padding: 8px 24px;
    border: none;
    border-radius: 10px;
    background: var(--Gold-Gradient-01);
    box-shadow: 0 4px 6.15px rgba(0, 0, 0, 0.21);
    color: #fff;
    font: inherit;
    font-weight: 500;
    text-transform: uppercase;
    transition: all 200ms ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  button.secondary {
    background: transparent;
    box-shadow: none;
    border: 1px solid #262c65;
    color: #262c65;
  }

  ${SIZES.mobile} {
    .modal-content {
      padding: 24px 16px 16px;
    }
    h5 {
      font-size: 20px;
    }
    p {
      font-size: 14px;
    }
    .actions {
      flex-direction: column-reverse;
    }
    button {
      width: 100%;
    }
  }
`

type NoticeVariant = 'welcome' | 'logout' | 'leave'

/**
 * Attendance reminder required by the organiser:
 * - `welcome`: right after login, once the participant lands in the lobby
 * - `logout`: before logging out, so they have to acknowledge it first
 * - `leave`: before navigating away from the Plenary Hall (see
 *   PlenaryLeaveGuard), and when they come back from another tab
 */
export default function AttendanceNoticeModal({
  showModal,
  hideModal,
  currentUser,
  variant = 'welcome',
  onConfirm,
  confirmLabel = 'OK',
}: {
  showModal: boolean
  hideModal: () => void
  currentUser?: CurrentUserType
  variant?: NoticeVariant
  onConfirm?: () => void
  confirmLabel?: string
}) {
  const dispatch = useDispatch()
  const location = useLocation()
  const isLogout = variant === 'logout'
  // Both leaving variants need an explicit "stay" escape hatch.
  const hasCancel = isLogout || variant === 'leave'

  const confirmLogout = () => {
    Amplitude.clickLogout()
    hideModal()
    logoutUser(dispatch, currentUser, location.pathname, 'You have logged out')
  }

  const handleConfirm = () => {
    if (isLogout) return confirmLogout()
    if (onConfirm) return onConfirm()
    hideModal()
  }

  return (
    <StyledModal show={showModal} onHide={hideModal} backdrop="static" keyboard={false} centered>
      <Modal.Body>
        <h5>{hasCancel ? 'Before You Leave' : 'Attendance Reminder'}</h5>
        <p>{ATTENDANCE_NOTICE}</p>
        <div className="actions">
          {hasCancel && (
            <button className="secondary" type="button" onClick={hideModal}>
              {variant === 'leave' ? 'Stay' : 'Cancel'}
            </button>
          )}
          <button type="button" onClick={handleConfirm}>
            {confirmLabel}
          </button>
        </div>
      </Modal.Body>
    </StyledModal>
  )
}
